import React from 'react';
import { Formik, Form, FormikValues } from 'formik';
import { Grid, Button, Typography } from '@material-ui/core';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import LinkIcon from '@material-ui/icons/Link';
import LockIcon from '@material-ui/icons/Lock';

import { ItunesSearch, TextInput, Spacing } from 'components';
import useGlobalStyles from 'hooks/useGlobalStyles';
import useAuth from 'hooks/useAuth';

import { signupValidationSchema, signupContinueValidationSchema } from './ValidationSchema';

interface Props {
  isContinuing?: boolean;
  onSubmit: (values: FormikValues, itunesId: string, feedUrl: string) => void;
}

const SignupForm: React.FC<Props> = (props) => {
  const { isContinuing, onSubmit } = props;
  const globalClasses = useGlobalStyles();
  const { signOut } = useAuth();

  // Local State
  const submitText = isContinuing ? 'Continue' : 'Get Started';
  const initialValues = isContinuing
    ? { vanityUrl: '' }
    : { vanityUrl: '', email: '', password: '' };
  const initialErrors = isContinuing
    ? { vanityUrl: '' }
    : { vanityUrl: '', email: '', password: '' };
  const validationSchema = isContinuing ? signupContinueValidationSchema : signupValidationSchema;

  const [selectedShowId, setSelectedShowId] = React.useState<string | undefined>(undefined);
  const [selectedShowFeed, setSelectedShowFeed] = React.useState<string | undefined>(undefined);

  const handleShowChange = (showId?: string, feedUrl?: string) => {
    setSelectedShowId(showId);
    setSelectedShowFeed(feedUrl);
  };

  const handleGoToSignin = async () => {
    if (isContinuing) {
      await signOut();
    }
  };

  const handleSubmit = (values: FormikValues) => {
    onSubmit(values, selectedShowId || '', selectedShowFeed || '');
  };

  return (
    <Formik
      initialValues={initialValues}
      initialErrors={initialErrors}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, values, handleChange, handleBlur, touched, errors }) => (
        <Form>
          <Grid className={globalClasses.gridNoSpacing} container spacing={0} direction="column">
            {isContinuing && (
              <Grid item>
                <Typography variant="h2" align="center">
                  Continue Account Setup
                </Typography>
                <Spacing height="32px" />
              </Grid>
            )}
            <Grid item>
              <ItunesSearch onChange={handleShowChange} />
            </Grid>
            <Grid item>
              <TextInput
                title="Vanity URL"
                name="vanityUrl"
                placeholder="URL"
                value={values.vanityUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                errorMessage={touched.vanityUrl && errors.vanityUrl}
                endAdornment={<LinkIcon />}
                startAdornment="pod.link/"
              />
            </Grid>
            {!isContinuing && (
              <>
                <Grid item>
                  <TextInput
                    title="Email"
                    name="email"
                    placeholder="person@example.com"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorMessage={touched.email && errors.email}
                    endAdornment={<AccountCircleIcon />}
                  />
                </Grid>
                <Grid item>
                  <TextInput
                    title="Password"
                    name="password"
                    placeholder="********"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorMessage={touched.password && errors.password}
                    endAdornment={<LockIcon />}
                    type="password"
                  />
                </Grid>
              </>
            )}
            <Grid item>
              <Grid container>
                <Grid item xs={12} sm={6}>
                  <Button variant="outlined" onClick={handleGoToSignin} href="/signin">
                    Sign In
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!isValid || selectedShowId === undefined}
                    onClick={() => handleSubmit(values)}
                  >
                    {submitText}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default SignupForm;
